import {flow, pipe} from 'fp-ts/lib/function'
import {ReadonlyRecord} from 'fp-ts/lib/ReadonlyRecord'
import React, {useCallback, useState} from 'react'
import {ReadNextLine} from 'read-next-line'
import {parseFile} from 'shared/dist/effects/parse-csv'
import {E, RA, RNEA, TE} from 'shared/dist/fp-ts-imp'
import {runEffect} from 'shared/dist/fp-ts-util'
import {tapConsole, tapConsoleTE} from 'shared/dist/utils/debug'
import * as t from 'io-ts'
import {createUserTE, UserPortal, UserPortalC} from '../dataProvider'
import {Button, useGetList} from 'react-admin'
import {vROrgId} from 'shared/dist/types/Organization'
import Select, {SelectChangeEvent} from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import {InputLabel} from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import {organizations} from '../App'

const processStream = async (
  stream: ReadableStream,
): Promise<ReadonlyArray<string>> => {
  const reader = new ReadNextLine(stream)

  const lines: Array<string> = []
  let stop = false
  while (!stop) {
    const line = await reader.readLine()
    stop = !line
    console.log(line)
    if (line) lines.push(line)
  }
  return lines
}

const processStreamTE = (stream: ReadableStream) =>
  TE.tryCatch(
    () => processStream(stream),
    reason =>
      reason instanceof Error ? reason.message : JSON.stringify(reason),
  )

const UserCSVImportInput: React.FC<{
  onImportComplete: () => void
  onCancelClick: () => void
}> = ({onCancelClick, onImportComplete}) => {
  const [lines, setLines] = useState<ReadonlyArray<UserPortal>>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const {data: orgs, isFetched} = useGetList(organizations)
  const [orgId, setOrgId] = useState<string>('')
  console.log(lines)
  console.log(errorMsg)

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) return
      const file = files[0]
      pipe(
        processStreamTE(file.stream()),
        TE.chain(
          flow(
            RNEA.fromReadonlyArray,
            TE.fromOption(() => 'csv file is empty'),
          ),
        ),
        TE.chainEitherKW(parseFile(UserPortalC)),
        TE.map(setLines),
        TE.mapLeft(setErrorMsg),
        runEffect,
      )
    },
    [],
  )

  const onConfirmClick = useCallback(() => {
    pipe(
      lines,
      RA.map(l => ({...l, organizationId: orgId})),
      TE.traverseArray(createUserTE),
      TE.mapLeft(setErrorMsg),
      TE.map(() => onImportComplete()),
      runEffect,
    )
  }, [lines, orgId])

  return (
    <div style={{padding: 20}}>
      <h4>Select CSV file with users to import:</h4>
      <br />
      <input type="file" accept=".csv" onChange={handleFileChange} />
      <p>
        <br />
        <a href="https://test.com">download CSV template</a>
        <br />
      </p>
      CSV file rules:
      <ul>
        <li>phoneNbr and email fields must be unique</li>
        <li>all values in doublequoates, even numbers: "101"</li>
        <li>all fields in the template are required</li>
      </ul>
      {errorMsg && (
        <p style={{color: 'red'}}>Failed to parse CSV file: {errorMsg}</p>
      )}
      {lines.length > 0 && (
        <div style={{paddingTop: 20}}>
          <InputLabel id="demo-simple-select-helper-label">
            Select Organization
          </InputLabel>
          <Select
            labelId="demo-simple-select-helper-label"
            value={orgId}
            style={{width: 150}}
            label="Select Organization"
            onChange={(event: SelectChangeEvent) => {
              setOrgId(event.target.value)
            }}>
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {orgs &&
              orgs.map((o: {id?: string; name?: string}) => (
                <MenuItem value={o.id}>{o.name}</MenuItem>
              ))}
          </Select>
          <h6>Users to import:</h6>
          <TableContainer component={Paper}>
            <Table sx={{minWidth: 650}}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => (
                  <TableRow
                    key={l.name}
                    sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                    <TableCell component="th" scope="row">
                      {l.name}
                    </TableCell>
                    <TableCell>{l.phoneNbr}</TableCell>
                    <TableCell>{l.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
      <div>
        <Button
          style={{padding: 20}}
          disabled={lines.length === 0 || !orgId}
          label="confirm"
          onClick={onConfirmClick}
        />
        <Button style={{padding: 20}} label="cancel" onClick={onCancelClick} />
      </div>
      {/* {fileName && <p>Selected file: {fileName}</p>}
      {fileContent && (
        <div>
          <h3>File Content:</h3>
          <pre>{fileContent}</pre>
        </div>
      )} */}
    </div>
  )
}

export default UserCSVImportInput
